import axios from "axios";
import { DOMAIN } from "../constants";

/**
 * Central access-token management: reads the stored session, proactively
 * refreshes the JWT before it expires, and forces logout when the session is
 * no longer valid. Used by the OpenAPI client (via OpenAPI.TOKEN), AccountsApi
 * and the Excel export helper so token handling lives in exactly one place.
 */

type UserInfo = {
  access?: string;
  refresh?: string;
  [key: string]: any;
};

export function getUserInfo(): UserInfo | null {
  try {
    const raw = localStorage.getItem("userInfo");
    return raw ? (JSON.parse(raw) as UserInfo) : null;
  } catch {
    return null;
  }
}

function setUserInfo(info: UserInfo) {
  localStorage.setItem("userInfo", JSON.stringify(info));
}

export function forceLogout() {
  localStorage.removeItem("userInfo");
  if (window.location.pathname !== "/login") {
    window.location.href = "/login";
  }
}

function decodeJwtExp(token: string): number | null {
  try {
    const payload = JSON.parse(
      atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
}

// Ensures only one refresh request is in flight at a time.
let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const info = getUserInfo();
  if (!info?.refresh) return null;
  try {
    const { data } = await axios.post(`${DOMAIN}/api/accounts/refresh_token/`, {
      refresh: info.refresh,
    });
    const updated: UserInfo = { ...info, access: data.access };
    // SIMPLE_JWT has ROTATE_REFRESH_TOKENS enabled, so a new refresh may arrive.
    if (data.refresh) updated.refresh = data.refresh;
    setUserInfo(updated);
    return data.access as string;
  } catch {
    return null;
  }
}

/**
 * Returns a valid access token, refreshing it first if it is missing or within
 * 60s of expiry. If the session can't be refreshed, the user is logged out.
 */
export async function getValidAccessToken(): Promise<string> {
  const info = getUserInfo();
  const token = info?.access;
  if (!token) return "";

  const exp = decodeJwtExp(token);
  const now = Date.now() / 1000;
  if (exp && exp - now > 60) {
    return token; // still comfortably valid
  }

  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  const refreshed = await refreshPromise;
  if (refreshed) return refreshed;

  forceLogout();
  return "";
}
